/**
 * The server keeps your history and faction share up to date in the background (tracking). Only
 * when it can't (tracking off, or no working key there yet) does the open site do it itself:
 *  - a stats snapshot at most once an hour (history on);
 *  - your share for your faction every 5 minutes (sharing on), unless the extension is signed in
 *    as you here — then it does that, and the site doesn't double the Torn calls.
 */
import { buildSnapshot, keyMeets, type KeyNeed, type ShareKey } from '@omni-torn/shared';
import { extension } from './bridge';
import { readBars, readCooldowns, readRefills, readStatus, readTravel, readBattleStats } from './read';
import { server, session, torn } from './session';

const HOUR = 3_600_000;
let lastSnap = 0;
let lastShare = 0;
let timer: ReturnType<typeof setInterval> | null = null;

const can = (need: KeyNeed) => keyMeets(session.get()?.access ?? null, need);

export async function snapshotNow(): Promise<boolean> {
  const sels = ['profile', 'personalstats', ...(can({ level: 3, user: ['battlestats'] }) ? ['battlestats'] : []), ...(can({ level: 3, user: ['networth'] }) ? ['networth'] : []), ...(can({ level: 3, user: ['money'] }) ? ['money'] : [])];
  const body = await torn('/user/', { selections: sels.join(',') }, 60_000);
  const snap = buildSnapshot(body, Date.now());
  if (!snap) return false;
  const r = await server<{ kept: boolean }>('POST', '/api/me/snapshot', { snapshot: snap });
  if (r.kept) lastSnap = Date.now();
  return r.kept;
}

/** Your share for your faction, read from Torn now (only the fields you ticked). */
export async function shareNow(): Promise<{ ok: boolean; message: string }> {
  const me = session.me();
  if (!me?.prefs.share.on || !me.factionId) return { ok: false, message: 'Sharing is off.' };
  const fields = new Set<ShareKey>(me.prefs.share.fields);
  const need: Record<string, KeyNeed> = { bars: { level: 2, user: ['bars'] }, cooldowns: { level: 2, user: ['cooldowns'] }, refills: { level: 2, user: ['refills'] }, travel: { level: 2, user: ['travel'] }, status: { level: 1, user: ['profile'] }, stats: { level: 3, user: ['battlestats'] } };
  const sel = new Set<string>();
  for (const f of fields) if (need[f] && can(need[f]!)) need[f]!.user!.forEach((x) => sel.add(x));
  if (!sel.size) return { ok: false, message: 'Your key can’t read what you chose to share.' };
  const now = Date.now();
  const body = await torn('/user/', { selections: [...sel].join(',') }, 60_000);
  const data: Record<string, unknown> = { at: now };
  if (sel.has('bars')) {
    const b = readBars(body, now);
    data.bars = Object.fromEntries((['energy', 'nerve', 'happy', 'life'] as const).filter((k) => b[k]).map((k) => [k, b[k]]));
  }
  if (sel.has('cooldowns')) {
    const c = readCooldowns(body);
    if (c) data.cooldowns = { drug: c.drug > 0 ? now + c.drug * 1000 : null, booster: c.booster > 0 ? now + c.booster * 1000 : null, medical: c.medical > 0 ? now + c.medical * 1000 : null };
  }
  if (sel.has('refills')) {
    const r = readRefills(body);
    if (r) data.refills = { energy: !r.energy, nerve: !r.nerve };
  }
  if (sel.has('travel')) {
    const t = readTravel(body);
    data.travel = t && (t.timeLeft > 0 || t.destination !== 'Torn') ? { destination: t.destination, departedAt: t.departed, arrivalAt: t.timeLeft > 0 ? t.arrival : null } : null;
  }
  if (sel.has('profile')) {
    const st = readStatus(body);
    if (st) data.status = { state: st.state, description: st.description, until: st.until };
  }
  if (sel.has('battlestats')) data.stats = readBattleStats(body)?.total ?? null;
  const r = await server<{ ok: boolean; message: string }>('POST', '/api/coord/share', { data });
  if (r.ok) lastShare = now;
  return r;
}

async function tick(): Promise<void> {
  const s = session.get();
  const me = session.me();
  if (!s || !me || document.hidden) return;
  // Asked fresh each time: the extension may have been signed out (or in) since.
  const ext = await extension(true);
  const extIsMe = !!ext?.signedIn && ext.playerId === s.playerId;
  // The server tracks you in the background (it keeps your history and your faction share going).
  const serverTracks = me.tracking.tracked && me.tracking.status === 'ok';
  try {
    // Otherwise the extension does these on its own when it's signed in as you (sharing: when it can share at your level).
    if (!serverTracks && !extIsMe && me.prefs.history !== false && Date.now() - Math.max(lastSnap, me.history.last ?? 0) > HOUR) await snapshotNow();
    // Sharing your own data with faction mates needs no invite (owner's call 2026-10-20).
    if (!serverTracks && !(extIsMe && ext?.canShare) && me.prefs.share.on && me.factionId && Date.now() - lastShare > 5 * 60_000) await shareNow();
  } catch {
    /* next time */
  }
}

export function startSync(): void {
  if (timer) return;
  void tick();
  timer = setInterval(() => void tick(), 60_000);
}

export function stopSync(): void {
  if (timer) clearInterval(timer);
  timer = null;
  lastSnap = 0;
  lastShare = 0;
}
